import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useT } from '../lib/i18n.js';
import { useSession } from '../stores/session.js';
import { errorMessage } from '../lib/properties.js';
import {
  getKnKeyState,
  removeKnApiKey,
  saveKnApiKey,
  verifyKnApiKey,
  type KnKeyStateDto,
} from '../lib/kn-settings-api.js';

/**
 * Krok KN1 bod 2 — Nastavení -> Integrace -> „Katastr nemovitostí".
 *
 * Vzhled bez novych komponent (`rh-card`, `rh-a2-link`), stejne jako
 * `AssistantConnectorsCard` ve stejne zalozce. Menit smi owner/admin -
 * tataz policy jako na serveru (`identity.tenant.update`).
 *
 * KLIC SE NIKDY NEVYPISUJE. Pole je `type="password"`, po ulozeni se
 * vyprazdni a ven uz chodi jen `secretHint` ze serveru.
 */
/**
 * Klic hlasky ze serveru prichazi jako retezec - prelozi se jen tehdy, kdyz
 * je to OPRAVDU jeden z klicu katalogu. Cizi retezec by jinak skoncil v UI
 * jako „dd.apiKn.error.cosi" (nebo by shodil stranku, viz pouceni S17i).
 */
const KN_ERROR_KEYS = [
  'dd.apiKn.error.offline',
  'dd.apiKn.error.no_key',
  'dd.apiKn.error.limit',
  'dd.apiKn.error.invalid_key',
  'dd.apiKn.error.forbidden',
  'dd.apiKn.error.bad_request',
  'dd.apiKn.error.not_found',
  'dd.apiKn.error.server',
  'dd.apiKn.error.network',
  'dd.apiKn.error.rejected',
] as const;

type KnErrorKey = (typeof KN_ERROR_KEYS)[number];

function knErrorKey(value: string | null): KnErrorKey {
  return KN_ERROR_KEYS.find((key) => key === value) ?? 'dd.apiKn.error.invalid_key';
}

export function KatastrSettingsCard(): React.JSX.Element | null {
  const t = useT();
  const user = useSession((s) => s.user);
  const queryClient = useQueryClient();
  const isOwnerOrAdmin = (user?.roles ?? []).some((r) => r === 'owner' || r === 'admin');

  const state = useQuery<KnKeyStateDto>({ queryKey: ['nastaveni', 'kn-key'], queryFn: getKnKeyState });
  const [draft, setDraft] = React.useState('');
  const [note, setNote] = React.useState<string | null>(null);
  const [usage, setUsage] = React.useState<{ used: number; limit: number } | null>(null);

  const key = state.data?.key ?? null;

  /**
   * Krok KN1b — podminky API KN zakazuji predat klic „jine osobe nebo
   * sluzbe", funkce je proto vypnuta flagem `KN_OFFICE_KEY_ENABLED`. Dokud
   * neznáme odpoved serveru (`undefined`), karta se jeste nevykresluje -
   * at nebliká „zapnuto" a hned zmizí.
   */
  const enabled = state.data?.enabled ?? false;

  async function onSave(): Promise<void> {
    setNote(null);
    try {
      await saveKnApiKey(draft.trim());
      setDraft('');
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'kn-key'] });
      setNote(t('webAdmin.settings.knSaved'));
    } catch (e) {
      setNote(t('webAdmin.settings.knSaveError', { error: errorMessage(e) }));
    }
  }

  async function onVerify(): Promise<void> {
    setNote(null);
    try {
      const result = await verifyKnApiKey(draft.trim());
      if (!result.ok) {
        setNote(t(knErrorKey(result.messageKey)));
        return;
      }
      const used = result.state?.callsUsed ?? null;
      const limit = result.state?.callLimit ?? state.data?.serviceDailyLimit ?? null;
      if (used !== null && limit !== null) setUsage({ used, limit });
      setNote(t('webAdmin.settings.knSaved'));
    } catch (e) {
      setNote(t('webAdmin.settings.knSaveError', { error: errorMessage(e) }));
    }
  }

  async function onRemove(): Promise<void> {
    setNote(null);
    try {
      await removeKnApiKey();
      await queryClient.invalidateQueries({ queryKey: ['nastaveni', 'kn-key'] });
      setNote(t('webAdmin.settings.knRemoved'));
    } catch (e) {
      setNote(t('webAdmin.settings.knSaveError', { error: errorMessage(e) }));
    }
  }

  /**
   * „Dnes použito X z Y" — po Overeni klice je autoritou odpoved ČÚZK
   * (`usage`), jinak posledni ulozeny stav. Kdyz nevime nic, rekneme to,
   * misto abychom tiskli nulu, ktera by lhala.
   */
  const shownUsage =
    usage ??
    (key !== null && key.callsUsed !== null && key.callLimit !== null
      ? { used: key.callsUsed, limit: key.callLimit }
      : null);

  if (!enabled) return null;

  return (
    <div className="rh-card" style={{ padding: 16, marginBottom: 16 }} data-testid="nastaveni-kn-card">
      <h2 className="mb-1 text-base font-semibold">{t('webAdmin.settings.knTitle')}</h2>
      <p className="mb-3 text-sm text-gray-600">{t('webAdmin.settings.knPerex')}</p>

      {key === null ? (
        <p className="mb-2 text-sm text-gray-600" data-testid="kn-key-missing">
          {t('webAdmin.settings.knKeyMissing')}
        </p>
      ) : (
        <p className="mb-1 text-sm" data-testid="kn-key-present">
          {t('webAdmin.settings.knKeyPresent', { hint: key.secretHint })}
        </p>
      )}

      {key !== null && key.status === 'invalid' ? (
        <p className="mb-2 text-sm" data-testid="kn-key-invalid">
          {t('webAdmin.settings.knKeyInvalid')}
        </p>
      ) : null}

      {shownUsage === null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="kn-usage-unknown">
          {t('webAdmin.settings.knUsageUnknown')}
        </p>
      ) : (
        <p className="mb-2 text-xs text-gray-600" data-testid="kn-usage">
          {t('webAdmin.settings.knUsage', { used: shownUsage.used, limit: shownUsage.limit })}
        </p>
      )}

      {key !== null && key.keyExpiresAt !== null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="kn-key-expires">
          {t('webAdmin.settings.knKeyExpires', { date: key.keyExpiresAt.slice(0, 10) })}
        </p>
      ) : null}

      {key === null && state.data?.platform.available === true ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="kn-platform-usage">
          {t('webAdmin.settings.knPlatformUsage', {
            used: state.data.platform.usedToday,
            limit: state.data.platform.dailyLimit,
          })}
        </p>
      ) : null}

      <label className="mb-1 block text-sm font-medium" htmlFor="kn-api-key">
        {t('webAdmin.settings.knKeyLabel')}
      </label>
      <input
        id="kn-api-key"
        type="password"
        autoComplete="off"
        className="mb-2 w-full rounded border px-2 py-1 text-sm"
        placeholder={t('webAdmin.settings.knKeyPlaceholder')}
        value={draft}
        disabled={!isOwnerOrAdmin}
        data-testid="kn-key-input"
        onChange={(e) => setDraft(e.target.value)}
      />

      <div className="mb-2 flex items-center gap-3">
        <button
          type="button"
          className="rh-a2-link text-sm"
          disabled={!isOwnerOrAdmin || draft.trim().length < 10}
          data-testid="kn-key-save"
          onClick={() => void onSave()}
        >
          {t('webAdmin.settings.knSaveButton')}
        </button>
        <button
          type="button"
          className="rh-a2-link text-sm"
          disabled={!isOwnerOrAdmin || draft.trim().length < 10}
          data-testid="kn-key-verify"
          onClick={() => void onVerify()}
        >
          {t('webAdmin.settings.knVerifyButton')}
        </button>
        {key !== null ? (
          <button
            type="button"
            className="rh-a2-link text-sm"
            disabled={!isOwnerOrAdmin}
            data-testid="kn-key-remove"
            onClick={() => void onRemove()}
          >
            {t('webAdmin.settings.knRemoveButton')}
          </button>
        ) : null}
      </div>

      {note !== null ? (
        <p className="mb-2 text-xs text-gray-600" data-testid="kn-key-note">
          {note}
        </p>
      ) : null}

      <details className="text-sm">
        <summary className="cursor-pointer font-medium">{t('webAdmin.settings.knHowToTitle')}</summary>
        <p>{t('webAdmin.settings.knHowToStep1')}</p>
        <p>{t('webAdmin.settings.knHowToStep2')}</p>
        <p>{t('webAdmin.settings.knHowToStep3')}</p>
        <p className="text-gray-600">{t('webAdmin.settings.knHowToNote')}</p>
      </details>
    </div>
  );
}
